import { useCallback, useEffect, useState } from "react";
import type { ActivityType, WorkSprint } from "@torre/work-hours";
import { fetchPeople } from "../../work-hours/services/workHoursService";
import { fetchActivityCatalog, fetchSprints, type CatalogResponse } from "../services/activitiesService";

// ---------------------------------------------------------------------------
// Datos compartidos por las secciones de Actividades (épica #1299): catálogo,
// sprints y colaboradores. Cada hook carga al montar y expone `reload`.
// ---------------------------------------------------------------------------

export interface CatalogState {
  catalog: ActivityType[];
  meta: CatalogResponse["meta"] | null;
  loading: boolean;
  error: boolean;
  reload: () => void;
}

export function useCatalog(): CatalogState {
  const [catalog, setCatalog] = useState<ActivityType[]>([]);
  const [meta, setMeta] = useState<CatalogResponse["meta"] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => setTick((n) => n + 1), []);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(false);
    fetchActivityCatalog()
      .then((res) => {
        if (!alive) return;
        setCatalog(res.data);
        setMeta(res.meta);
      })
      .catch(() => alive && setError(true))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [tick]);
  return { catalog, meta, loading, error, reload };
}

export interface SprintsState {
  sprints: WorkSprint[];
  activeSprintId: string | null;
  loading: boolean;
  error: boolean;
  reload: () => void;
}

export function useSprints(): SprintsState {
  const [sprints, setSprints] = useState<WorkSprint[]>([]);
  const [activeSprintId, setActive] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => setTick((n) => n + 1), []);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(false);
    fetchSprints()
      .then((res) => {
        if (!alive) return;
        setSprints(res.data);
        setActive(res.meta.activeSprintId);
      })
      .catch(() => alive && setError(true))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [tick]);
  return { sprints, activeSprintId, loading, error, reload };
}

/** Colaborador tal como lo necesitan los selectores: id, nombre completo y puesto. */
export interface MemberLite {
  id: string;
  name: string;
  role: string | null;
}

/**
 * Gente asignable a un ticket. Sale de `GET /workHoursPeople` (Actividades: id,
 * nombre y puesto; nunca sueldo, tarifa, contrato ni datos bancarios), no del
 * módulo Equipo (`/teamMember*`), que en WAYAK está apagado. Si la lista no
 * llega, Actividades sigue funcionando sin responsables.
 */
export function useTeamMembersLite(): { members: MemberLite[]; loading: boolean } {
  const [members, setMembers] = useState<MemberLite[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    fetchPeople()
      .then((people) => {
        if (!alive) return;
        // El servidor ya deja fuera a quien salió del equipo (offboarded).
        const list = (people || [])
          .filter((p) => p && p.teamMemberId)
          .map((p) => ({ id: p.teamMemberId, name: p.name || p.teamMemberId, role: p.role ?? null }))
          .sort((a, b) => a.name.localeCompare(b.name));
        setMembers(list);
      })
      .catch(() => alive && setMembers([]))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);
  return { members, loading };
}
